import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SectionTitle from './SectionTitle';
import cemImg from '../assets/team/cem.png';
import denizImg from '../assets/team/deniz.png';
import erdemImg from '../assets/team/erdem.png';

const TeamContainer = styled.section`
  padding: 120px 56px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  position: relative;

  @media (max-width: 768px) {
    padding: 80px 20px;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  max-width: 860px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;

    > :last-child:nth-child(odd) {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const TeamMember = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Avatar = styled.div`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid ${({ theme }) => `rgba(${theme.colors.textRgb},0.08)`};
  margin-bottom: 20px;
  transition: border-color 200ms ease;

  ${TeamMember}:hover & {
    border-color: rgba(255,91,58,0.4);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 480px) {
    width: 160px;
    height: 160px;
  }
`;

const MemberName = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 6px;
`;

const MemberRole = styled.p`
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #FF8A6B;
  margin: 0;
`;

const team = [
  { name: 'Cem Sertkaya', role: 'Co-Founder | AI Application Engineer', image: cemImg },
  { name: 'Deniz Eren Gencay', role: 'Co-Founder | Agentic System Engineer', image: denizImg },
  { name: 'Erdem Cobanoglu', role: 'AI Product Engineer', image: erdemImg },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const memberVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
};

const TeamSection = () => (
  <TeamContainer id="team">
    <SectionTitle>Our Team</SectionTitle>
    <TeamGrid
      as={motion.div}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
    >
      {team.map((member) => (
        <TeamMember key={member.name} variants={memberVariants}>
          <Avatar>
            <img
              src={member.image}
              alt={member.name}
              loading="eager"
              decoding="async"
              fetchpriority="high"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </Avatar>
          <MemberName>{member.name}</MemberName>
          <MemberRole>{member.role}</MemberRole>
        </TeamMember>
      ))}
    </TeamGrid>
  </TeamContainer>
);

export default TeamSection;
